<script setup lang="ts">
  import { z } from 'zod';
  import { QuestionTypes } from '@borg/types';
  import { MpCard, MpInput, MpSelect, MpText, MpTextArea } from '@borg/ui';
  import CheckboxField from './CheckboxField.vue';
  import RadioField from './RadioField.vue';

  const emit = defineEmits<{
    submit: [];
  }>();

  const { t } = useI18n();
  const applicationForm = _applicationForm.inject();

  function getAnswers(idx: number, type: QuestionTypes) {
    const applicationDataQuestions = applicationForm.application?.data.questions ?? [];
    const applicationQuestionnaireQuestions =
      applicationForm.application?.questionnaire?.questions ?? [];

    if (type === 'textarea' || type === 'textfield') {
      return applicationDataQuestions[idx]?.answers[0]
        ? applicationDataQuestions[idx]?.answers
        : [{ id: applicationQuestionnaireQuestions[idx].choices[0].id, name: '' }];
    }

    return applicationDataQuestions[idx]?.answers || [];
  }

  function toFormData() {
    return (
      applicationForm.application?.questionnaire?.questions.map((q, idx) => ({
        id: q.id,
        answers: getAnswers(idx, q.type),
      })) || []
    );
  }

  function isValid(val: ReturnType<typeof toFormData>[number]) {
    const question = applicationForm.application?.questionnaire?.questions.find(
      (q) => q.id === val.id,
    );

    if (question?.isRequired) {
      if (question.type === 'textfield' || question.type === 'textarea') {
        return val.answers[0].name !== '';
      }

      return val.answers.length > 0;
    }

    return true;
  }

  const { values, state, validate } = useForm({
    immediate: false,
    initialData: () => toFormData(),
    schema: z
      .object({
        id: z.number(),
        answers: z
          .object({
            id: z.number().optional(),
            name: z.string(),
          })
          .array(),
      })
      .refine(isValid, t('REQUIRED_INPUT'))
      .array(),
  });

  async function onSubmit() {
    if (await validate()) {
      emit('submit');
    } else {
      scrollToTheNearestErrorField();
    }
  }

  function scrollToTheNearestErrorField() {
    const errorElement = document.querySelector(`*[class*="--error"]`);
    errorElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  watch(values, (questions) => applicationForm.saveDebounced({ questions }));
</script>

<template>
  <MpCard
    id="questionnaire-form"
    as="form"
    p="lg"
    class="form"
    border
    @submit.prevent="onSubmit"
  >
    <div
      v-for="(question, idx) in applicationForm.application?.questionnaire?.questions"
      :key="question.id"
    >
      <MpText
        type="h6"
        as="div"
        style="margin-bottom: var(--mp-space-30)"
      >
        {{ `${idx + 1}. ${question.question.trim()}${question.isRequired ? '&nbsp;*' : ''}` }}
      </MpText>
      <RadioField
        v-if="question.type === 'radio'"
        v-model="values[idx].answers"
        :options="question.choices"
        :error="state[idx].message"
      />
      <CheckboxField
        v-if="question.type === 'checkbox'"
        v-model="values[idx].answers"
        :options="question.choices"
        :error="state[idx].message"
      />
      <MpInput
        v-if="question.type === 'textfield'"
        v-model="values[idx].answers[0].name"
        :error="state[idx].message"
      />
      <MpTextArea
        v-if="question.type === 'textarea'"
        v-model="values[idx].answers[0].name"
        :error="state[idx].message"
      />
      <MpSelect
        v-if="question.type === 'select'"
        v-model="values[idx].answers[0]"
        :options="question.choices"
        :get-value="(q) => q.id"
        :get-label="(q) => q.name"
        :placeholder="$t('SELECT_OPTION')"
        :error="state[idx].message"
      />
    </div>
  </MpCard>
</template>

<style scoped lang="scss">
  .form {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-60);
  }
</style>
